import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Bot, Calculator, PlayCircle, Users, Store,
  ArrowRight, ExternalLink, CheckCircle2, Wrench
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { AcademyOptIn } from '../components/AcademyOptIn';
import { CONTACT } from '../lyphline';

const ACADEMY_URL = 'https://www.freezedryacademy.com';

const RESOURCES = [
  {
    icon: GraduationCap,
    title: 'Learning & Courses',
    copy: 'Structured lessons written by working operators, from freeze-drying fundamentals to advanced topics like running multiple machines in parallel. No fluff, no filler.',
  },
  {
    icon: Bot,
    title: 'AI Assistant',
    copy: 'Ask specific freeze-dry questions and get grounded, cited answers drawn from real freeze-dry science, instead of guessing your way through a stuck batch.',
  },
  {
    icon: Calculator,
    title: 'Business Tools',
    copy: 'Real batch math: margin, capacity, break-even and sensitivity calculators built for how freeze dryers actually behave, with one-click pro-forma PDFs for lenders or partners.',
  },
  {
    icon: PlayCircle,
    title: 'Video Library',
    copy: 'Curated tutorials focused on the things new operators get stuck on, so you can see the technique instead of only reading about it.',
  },
  {
    icon: Users,
    title: 'Community Forum',
    copy: 'Talk shop with operators who run the same machines you do. Trade cycle settings, troubleshooting wins and hard-won lessons.',
  },
  {
    icon: Store,
    title: 'Vendor Directory',
    copy: 'A vetted list of suppliers and consultants, mylar, oxygen absorbers, custom labels and more, so you can skip the marketplace noise.',
  },
];

const HIGHLIGHTS = [
  'Practical curriculum, not marketing',
  'AI assistant tuned to freeze-drying',
  'Batch, margin and capacity calculators',
  'Vendor and consultant directory',
  'Installable app, desktop + mobile',
  'Optional 1:1 consulting when you need it',
];

const FreezeDryAcademy = () => (
  <div className="min-h-screen bg-white">
    <SEO
      title="Freeze Dry Academy, Learning & Tools for Operators"
      description="Freeze Dry Academy gives freeze-dryer operators practical courses, an AI assistant trained on freeze-dry science, batch and margin calculators, a video library, a community forum and a vetted vendor directory."
      keywords="freeze dry academy, freeze drying courses, freeze dryer training, freeze drying calculator, freeze dry community, freeze dry operators"
    />
    <Navbar />

    {/* Hero */}
    <section className="pt-32 pb-16 section-padding bg-[#003456] relative overflow-hidden" data-testid="academy-hero">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #CE8946 0, transparent 40%), radial-gradient(circle at 80% 70%, #CE8946 0, transparent 45%)' }} />
      <div className="max-w-7xl mx-auto relative">
        <span className="inline-flex items-center gap-2 bg-[#CE8946]/15 text-[#CE8946] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] border border-[#CE8946]/30">
          <GraduationCap className="h-4 w-4" /> Learn the craft
        </span>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-6 max-w-3xl leading-[1.1]">
          Freeze Dry Academy
        </h1>
        <p className="text-lg text-white/75 mt-5 max-w-2xl leading-relaxed">
          A workbench for freeze dry operators. Real batch math, an AI assistant trained on freeze dry
          science, and a community of people who run the same machines you do. We point our customers here to
          get the most out of every rebuild.
        </p>
        <div className="flex flex-wrap gap-4 mt-8">
          <a href={ACADEMY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors shadow-xl shadow-[#CE8946]/20" data-testid="academy-hero-visit">
            Visit Freeze Dry Academy <ExternalLink className="h-5 w-5" />
          </a>
          <Link to="/documents" className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors" data-testid="academy-hero-downloads">
            Our downloads &amp; guides <ArrowRight className="h-5 w-5 text-[#CE8946]" />
          </Link>
        </div>
      </div>
    </section>

    {/* Resources grid */}
    <section className="py-20 section-padding bg-white" data-testid="academy-resources">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-12">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">What you'll find there</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Everything you need to run better batches</h2>
          <p className="text-slate-600 mt-3">Built by operators, for the next ones, without the upsells or spam.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCES.map((r) => (
            <div key={r.title} className="group bg-[#F1F5F9] border border-slate-200 rounded-2xl p-8 hover:shadow-lg hover:border-[#CE8946]/50 transition-all" data-testid={`academy-resource-${r.title.split(' ')[0].toLowerCase()}`}>
              <span className="w-14 h-14 rounded-xl bg-[#003456] flex items-center justify-center mb-5 group-hover:bg-[#CE8946] transition-colors">
                <r.icon className="h-7 w-7 text-[#CE8946] group-hover:text-[#003456] transition-colors" />
              </span>
              <h3 className="text-lg font-bold text-[#003456]">{r.title}</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{r.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Highlights + CTA band */}
    <section className="py-20 section-padding bg-[#003456]" data-testid="academy-highlights">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Why we recommend it</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Built by operators, for the next ones</h2>
          <p className="text-white/70 mt-4 leading-relaxed">
            A rebuilt machine is only half the story, knowing how to run it well is the other half. Freeze Dry
            Academy is where we send customers who want to sharpen their process and grow their output.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-8">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-2 text-white/80 text-sm">
                <CheckCircle2 className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" /> {h}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[#023F63] border border-white/10 rounded-2xl p-8">
          <GraduationCap className="h-10 w-10 text-[#CE8946]" />
          <h3 className="text-2xl font-bold text-white mt-4">Ready to level up your batches?</h3>
          <p className="text-white/60 mt-3 text-sm leading-relaxed">
            Drop your email to jump into Freeze Dry Academy. We'll open it for you and keep you in the loop on
            rebuild tips and offers, most of the Academy starts free.
          </p>
          <div className="mt-6">
            <AcademyOptIn variant="dark" source="academy-page-optin" testid="academy-page-optin" />
          </div>
          <p className="text-white/40 text-xs mt-4">freezedryacademy.com opens in a new tab.</p>
        </div>
      </div>
    </section>

    {/* Machine trouble CTA */}
    <section className="py-16 section-padding bg-white" data-testid="academy-repair-cta">
      <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#003456] to-[#023F63] rounded-2xl px-6 sm:px-10 py-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <h3 className="text-xl sm:text-2xl font-bold text-white">Machine giving you trouble, not just questions?</h3>
          <p className="text-white/65 text-sm mt-1.5">We rebuild and modify freeze dryers of select makes and sizes. Start with a free diagnostic quote.</p>
        </div>
        <Link to="/repair/quote" className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3.5 rounded-md transition-colors whitespace-nowrap" data-testid="academy-repair-btn">
          <Wrench className="h-5 w-5" /> Free diagnostic quote
        </Link>
      </div>
    </section>

    <Footer />
  </div>
);

export default FreezeDryAcademy;
